import { ScrollBar } from '@yes.technology/react-toolkit'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import useStyleMaps from 'site/Renderer/hooks/useStyleMap'
import { FadeOverlay, Wrapper } from './InformationalFadingContainer.styles'
import { InformationalFadingContainerProps } from './InformationalFadingContainer.types'

export const INFORMATIONAL_FADING_CONTAINER_DEFAULT_MAX_HEIGHT = '200px'

const DEFAULT_FADE_COLOR = '#ffffff'

const computeIsAtBottom = (el: HTMLDivElement | null): boolean => {
  if (!el) {
    return true
  }

  const { scrollHeight, clientHeight, scrollTop } = el

  if (scrollHeight <= clientHeight) {
    return true
  }

  return scrollTop + clientHeight >= scrollHeight - 1
}

const InformationalFadingContainer = ({
  maxHeight = INFORMATIONAL_FADING_CONTAINER_DEFAULT_MAX_HEIGHT,
  scrollbarHeightClass = 'extra-small',
  styleMap,
  children
}: InformationalFadingContainerProps) => {
  const styleProps = useStyleMaps(styleMap)
  const scrollRef = useRef<HTMLDivElement>(null)
  const [isAtBottom, setIsAtBottom] = useState(true)

  const syncScrollState = useCallback(() => {
    setIsAtBottom(computeIsAtBottom(scrollRef.current))
  }, [])

  useEffect(() => {
    const rafId = requestAnimationFrame(syncScrollState)

    return () => {
      cancelAnimationFrame(rafId)
    }
  }, [maxHeight, syncScrollState])

  const handleScroll = useCallback((event: React.UIEvent<HTMLDivElement>) => {
    setIsAtBottom(computeIsAtBottom(event.currentTarget))
  }, [])

  const showFade = !isAtBottom

  return (
    <Wrapper $maxHeight={maxHeight} style={styleProps}>
      <ScrollBar
        ref={scrollRef}
        data-testid='informational-fading-container-scroll'
        heightClass={scrollbarHeightClass}
        scrollMode='vertical'
        onScroll={handleScroll}
        style={{ flex: 1, minHeight: 0 }}
      >
        {children}
      </ScrollBar>
      <FadeOverlay
        $fadeColor={DEFAULT_FADE_COLOR}
        $visible={showFade}
        data-testid='informational-fading-container-fade'
        aria-hidden='true'
      />
    </Wrapper>
  )
}

export default InformationalFadingContainer
